import React from "react";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { CheckCircle2, Tag, Plus, Banknote } from "lucide-react";
import { useToast } from "@shared/ui/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useApplyCommonServices } from "../../../hooks";

interface ChoseFieldsProps {
  data: any;
  onDataChange: (data: any) => void;
  onNext: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  allStepData?: any;
}

const FIELD_OPTIONS = [
  {
    id: "category",
    title: "Category",
    description: "Reassign each record to a different consumer category.",
  },
  {
    id: "effectiveFrom",
    title: "Valid From",
    description: "Shift the activation date of every selected record.",
  },
  {
    id: "endDate",
    title: "Valid To",
    description: "Set or extend the end of the billing window.",
  },
  {
    id: "setTariff",
    title: "Set Tariff for Common Service",
    description: "Replace the existing tariff on a service that all selected plans share. Common: EV Charging, Electricity.",
    icon: <Tag className="w-4 h-4 text-blue-500" />,
    iconBg: "bg-blue-100",
  },
  {
    id: "addCharge",
    title: "Add a Charge",
    description: "Append a new Fixed, Surcharge, or Tax line to every selected plan's charge configuration.",
    icon: <Plus className="w-4 h-4 text-indigo-500" />,
    iconBg: "bg-indigo-100",
  },
];

export const ChoseFields: React.FC<ChoseFieldsProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  allStepData,
}) => {
  const selectedFields = data?.selectedFields || [];
  const selectedPlansCount = allStepData?.[0]?.selectedRecords?.length || 0;
  const remoteUtilityId=useRemoteUtilityId();

  const { mutateAsync: applyCommonServices, isPending } = useApplyCommonServices();
  const { toast } = useToast();

  const toggleField = (id: string) => {
    if (selectedFields.includes(id)) {
      onDataChange?.({ ...data, selectedFields: selectedFields.filter((f: string) => f !== id) });
    } else {
      onDataChange?.({ ...data, selectedFields: [...selectedFields, id] });
    }
  };

  const handleNext = async () => {
    if (selectedFields.includes("setTariff")) {
      const planIds = allStepData?.[0]?.selectedRecords?.map(Number) || [];
      try {
        const response = await applyCommonServices({ plan_ids: planIds,remote_utility_id: remoteUtilityId });
        onDataChange?.({ ...data, selectedFields, commonServices: response });
        setTimeout(() => {
          onNext();
        }, 50);
      } catch (error: any) {
        console.error(error);
        toast({
          title: "Error",
          description: error.message || "Failed to fetch common services.",
          variant: "destructive"
        });
      }
    } else {
      onNext();
    }
  };

  return (
    <div className="bg-white p-0">
      <div className="mb-6">
        <PageHeader
          title="Choose Fields to Edit"
          subtitle={`Toggle on the fields you want to change across all ${selectedPlansCount} selected plans. Untoggled fields are left untouched.`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {FIELD_OPTIONS.slice(0, 3).map((field) => {
          const isSelected = selectedFields.includes(field.id);
          return (
            <div
              key={field.id}
              onClick={() => toggleField(field.id)}
              className={`relative p-4 border rounded-xl cursor-pointer transition-colors ${
                isSelected ? "border-blue-500 bg-blue-50" : "border-blue-200 bg-white hover:border-blue-300"
              }`}
            >
              <h4 className="font-semibold text-gray-900 text-sm mb-1 pr-6">{field.title}</h4>
              <p className="text-xs text-gray-500 pr-6">{field.description}</p>
              {isSelected && (
                <CheckCircle2 className="w-5 h-5 text-blue-500 absolute top-4 right-4 fill-white" />
              )}
            </div>
          );
        })}
      </div>

      <h3 className="text-xs font-semibold text-gray-500  tracking-wider mt-8 mb-4">
        Plan-Only Operations
      </h3>

      <div className="space-y-4">
        {FIELD_OPTIONS.slice(3).map((field) => {
          const isSelected = selectedFields.includes(field.id);
          return (
            <div
              key={field.id}
              onClick={() => toggleField(field.id)}
              className={`relative p-4 border rounded-xl cursor-pointer flex gap-4 items-center transition-colors ${
                isSelected ? "border-blue-500 bg-blue-50" : "border-blue-200 bg-white hover:border-blue-300"
              }`}
            >
              <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${field.iconBg}`}>
                {field.icon}
              </div>
              <div className="pr-6">
                <h4 className="font-semibold text-gray-900 text-sm mb-1">{field.title}</h4>
                <p className="text-xs text-gray-500">{field.description}</p>
              </div>
              {isSelected && (
                <CheckCircle2 className="w-5 h-5 text-blue-500 absolute top-4 right-4 fill-white" />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-between space-x-3 border-t mt-8 pt-6">
        {!isFirstStep && (
          <Button
           variant="outline"
            onClick={onPrevious}
            className=""
          >
            Previous
          </Button>
        )}
        <Button
         variant="default"
            onClick={handleNext}
            disabled={isPending}
            className=""
        >
          {isPending ? "Loading..." : isLastStep ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};

export default ChoseFields;
